import { useState } from 'react';

import type { CashAccountBalance, CashAccountChoice, CashVoucher } from '@/api/cash';
import type { CashVoucherKind } from '@/api/generated/enums';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Button, Card, Notice, PageHeading } from '@/components/PageHeading';
import { Field, controlClass } from '@/components/Field';
import { SplitBoxes } from '@/components/SplitBoxes';
import { useSplitParts } from '@/components/useSplitParts';
import { paymentMethodLabel } from '@core/bookings/paymentWords';
import {
  filledParts,
  splitTotal,
  splitWords,
  withoutSaved,
  type PaidInParts,
  type SplitPart,
} from '@core/cash/split';
import {
  emptyVoucher,
  sideOf,
  voucherBase,
  voucherKindHints,
  voucherKindWords,
  voucherPartsProblem,
  type VoucherDraft,
} from '@core/cash/voucher';
import { formatAmount } from '@core/format/money';

const rupees = (value: number) => `₹${formatAmount(value)}`;
const methodWord = (method: SplitPart['method']) =>
  method === 'Cash' ? 'cash' : paymentMethodLabel[method];

/**
 * Money in and Money out (Cash & bank step 4) — owner's money, owner's drawings, other income. One
 * page for both sides: the kinds a person may record are chosen first as tiles, then only the
 * fields that kind needs. An expense or a vendor payment has its own page and is pointed to, not
 * duplicated here.
 *
 * <p>
 * <strong>A page, not a drawer</strong>, reached by internal state from the cash book. **A box per
 * account** (split payments step 6, owner 2026-11-01): ₹20,000 into the till and ₹30,000 into the
 * bank is two vouchers, one per account — cash through a till or petty cash, the rest a bank.
 * </p>
 */
export function CashVoucherPage({
  kinds,
  side: fixedSide,
  accounts,
  today,
  busy = false,
  onCancel,
  onSave,
}: {
  /** What this person may record here, on this side. */
  kinds: CashVoucherKind[];
  /** Which verb this is, for when {@link kinds} is empty and cannot say. */
  side?: 'in' | 'out';
  accounts: CashAccountBalance[];
  /** The shop's date, `YYYY-MM-DD`. */
  today: string;
  busy?: boolean;
  onCancel: () => void;
  /** Answers what was saved; a refusal part-way leaves the page open, and the page says so. */
  onSave: (
    base: ReturnType<typeof voucherBase>,
    parts: readonly SplitPart[],
  ) => Promise<PaidInParts<CashVoucher>>;
}) {
  const firstField = useAutoFocusFirstField();
  const [draft, setDraft] = useState<VoucherDraft>(() =>
    emptyVoucher(kinds[0] ?? 'OtherIncome', today),
  );
  const [partly, setPartly] = useState<string | null>(null);
  const change = (next: Partial<VoucherDraft>) => setDraft((was) => ({ ...was, ...next }));

  const side = kinds.length > 0 ? sideOf(draft.kind) : (fixedSide ?? 'out');
  const hasForm = kinds.length > 0;
  const choices: CashAccountChoice[] = accounts
    .filter((account) => account.isActive)
    .map(({ id, name, kind, isDefault }) => ({ id, name, kind, isDefault }));
  const { parts, change: changeParts } = useSplitParts(choices);
  const total = splitTotal(parts);
  const used = filledParts(parts).length;
  const problem = voucherPartsProblem(draft, today, parts);
  const owner = draft.kind !== 'OtherIncome';
  const direction = draft.kind === 'OwnerOut' ? 'from' : 'into';

  const save = () => {
    if (problem !== null || busy) {
      return;
    }

    setPartly(null);
    void onSave(voucherBase(draft), parts)
      .then(({ saved, failed }) => {
        if (!failed) {
          return;
        }

        // What went through is emptied, so Save again sends only what is left.
        changeParts((current) => withoutSaved(current, saved));
        const words = splitWords(
          saved.map((each) => each.part),
          methodWord,
          rupees,
          direction,
        );
        setPartly(`${words} — saved. ${failed.part.accountName} was not.`);
      })
      // The refusal is already on the toast; nothing was saved.
      .catch(() => undefined);
  };

  return (
    <div className="flex flex-col gap-3 lg:gap-4" {...enterKeyScope}>
      <PageHeading
        title={side === 'in' ? 'Money in' : 'Money out'}
        subtitle={
          side === 'in'
            ? 'Money that is not a booking or a sale'
            : 'What the owner takes home. Shop spending and suppliers have their own pages.'
        }
        actions={
          <>
            <Button onClick={onCancel} disabled={busy} shortcut="back">
              Cancel
            </Button>
            {hasForm && (
              <Button
                variant="primary"
                icon="icon icon-check"
                data-enter-key-commit
                shortcut="save"
                disabled={problem !== null || busy}
                onClick={save}
              >
                {used === 0
                  ? 'Save'
                  : `Save ${rupees(total)}${used > 1 ? ` · ${used} vouchers` : ''}`}
              </Button>
            )}
          </>
        }
      />

      <div
        role="group"
        aria-label="What it is"
        className={`grid gap-2 ${kinds.length > 2 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}
      >
        {kinds.map((kind) =>
          kind === draft.kind ? (
            <div
              key={kind}
              className="border-primary bg-primary-transparent rounded-md border-2 p-3"
            >
              <p className="text-[13px] font-semibold text-gray-900">{voucherKindWords[kind]}</p>
              <p className="text-default mt-0.5 text-[11px]">{voucherKindHints[kind]}</p>
            </div>
          ) : (
            <button
              key={kind}
              type="button"
              disabled={busy}
              onClick={() => setDraft(emptyVoucher(kind, today))}
              className="border-border-color hover:bg-light cursor-pointer rounded-md border bg-white p-3 text-start"
            >
              <span className="block text-[13px] font-semibold text-gray-900">
                {voucherKindWords[kind]}
              </span>
              <span className="text-default mt-0.5 block text-[11px]">
                {voucherKindHints[kind]}
              </span>
            </button>
          ),
        )}
      </div>

      {hasForm && (
        <div className="grid gap-3 lg:grid-cols-2 lg:gap-4">
          <Card>
            <p className="mb-2.5 text-sm font-semibold text-gray-900">
              {side === 'in' ? 'How much, into which account' : 'How much, from which account'}
            </p>
            <div className="flex flex-col gap-3">
              <SplitBoxes
                parts={parts}
                onChange={changeParts}
                direction={direction === 'from' ? 'outOf' : 'into'}
                busy={busy}
              />
              <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                Two accounts make two vouchers, each in its own day book.
              </p>
            </div>
          </Card>

          <Card>
            <p className="mb-2.5 text-sm font-semibold text-gray-900">When, and whose</p>

            <div className="flex flex-col gap-3">
              <Field
                label="Day"
                required
                hint="The day the money moved. It cannot be in the future."
              >
                <input
                  ref={firstField}
                  type="date"
                  max={today}
                  disabled={busy}
                  value={draft.occurredOn}
                  onChange={(event) => change({ occurredOn: event.target.value })}
                  className={controlClass}
                />
              </Field>

              {owner && (
                <Field label="Whose money" hint="Blank means the company owner.">
                  <input
                    type="text"
                    disabled={busy}
                    value={draft.personName}
                    onChange={(event) => change({ personName: event.target.value })}
                    className={controlClass}
                  />
                </Field>
              )}

              <Field
                label={draft.kind === 'OwnerOut' ? 'What for' : 'Notes'}
                required={draft.kind === 'OwnerOut'}
              >
                <textarea
                  rows={2}
                  disabled={busy}
                  value={draft.notes}
                  onChange={(event) => change({ notes: event.target.value })}
                  className={controlClass}
                />
              </Field>

              <Field label="Reference" hint="On every voucher.">
                <input
                  type="text"
                  disabled={busy}
                  value={draft.reference}
                  onChange={(event) => change({ reference: event.target.value })}
                  className={controlClass}
                />
              </Field>
            </div>
          </Card>
        </div>
      )}

      {hasForm && partly && (
        <Notice tone="warning" title="Part of it was saved">
          {partly}
        </Notice>
      )}
      {hasForm && problem && used > 0 && (
        <p className="text-warning text-[length:var(--scale-list-meta)]">{problem}</p>
      )}
      {hasForm && used > 0 && !problem && (
        <p className="text-default text-[length:var(--scale-list-meta)]">
          {splitWords(parts, methodWord, rupees, direction)}.
        </p>
      )}
    </div>
  );
}
